import { useState } from "react";
import { Link } from "react-router-dom";
import type { EntitySummary } from "../../../shared/api";
import { api } from "../../lib/api";
import { Empty } from "../../components/States";

// Claiming (SPEC §6.1). Both methods derive from the entity's domain, so an
// entity with no website has no path — the UI says so rather than offering a
// button that cannot succeed.

export function Claim() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<EntitySummary[] | null>(null);
  const [target, setTarget] = useState<EntitySummary | null>(null);
  const [state, setState] = useState<{ claimable: boolean; methods: string[]; reason?: string } | null>(null);
  const [challenge, setChallenge] = useState<{ claimId: string; recordName: string; recordValue: string } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const search = async () => {
    setResults(null);
    const r = await api.search(q);
    setResults(r.items);
  };

  const select = async (e: EntitySummary) => {
    setTarget(e);
    setChallenge(null);
    setStatus(null);
    setErr(null);
    setState(await api.claimable(e.id));
  };

  const begin = async (method: string) => {
    if (!target) return;
    setErr(null);
    try {
      const started = await api.startClaim(target.id, method);
      if (method === "onchain_owner") {
        // The proof is a signature from the deployer address, so the record is
        // a message to sign rather than something to publish.
        setChallenge(started);
        return;
      }
      if (method === "email_domain") {
        // Nothing to publish: the domain match is the proof. Verify immediately.
        setChallenge(null);
        const result = await api.verifyClaim(started.claimId);
        setStatus(result.status);
      } else {
        setChallenge(started);
      }
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const check = async () => {
    if (!challenge) return;
    setErr(null);
    try {
      const result = await api.verifyClaim(challenge.claimId);
      setStatus(result.status);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <div className="narrow-md">
      <h2>Claim a profile</h2>

      <div className="row u-4eba7b6">
        <input
          className="input flex-1"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void search()}
          placeholder="Search for your company or token"
        />
        <button className="btn btn-primary" onClick={() => void search()}>Search</button>
      </div>

      {results && results.length === 0 && <Empty title="Nothing found" />}
      {results && results.length > 0 && !target && (
        <div className="grid gap-8">
          {results.map((e) => (
            <button key={e.id} className="card u-4d6c590" onClick={() => void select(e)}>
              <strong>{e.name}</strong>{" "}
              <span className="muted">{e.claimStatus === "claimed" ? "· already claimed" : ""}</span>
            </button>
          ))}
        </div>
      )}

      {target && state && (
        <section className="card u-cc4d67f">
          <div className="row between">
            <h3 className="m-0">{target.name}</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => { setTarget(null); setState(null); }}>
              Change
            </button>
          </div>

          {!state.claimable && (
            <p className="muted">
              {state.reason === "no_proof_available"
                ? "This token has no verified website and no known deployer address, so there is nothing we can check a claim against yet."
                : "This listing has no verified website, so it can't be claimed automatically yet. Domain verification is the only path for companies."}
            </p>
          )}

          {state.claimable && !challenge && !status && (
            <div className="row gap-8 mt-12">
              {state.methods.includes("dns_txt") && (
                <button className="btn btn-primary" onClick={() => void begin("dns_txt")}>
                  Verify by DNS record
                </button>
              )}
              {state.methods.includes("email_domain") && (
                <button className="btn btn-ghost" onClick={() => void begin("email_domain")}>
                  Verify by company email
                </button>
              )}
              {state.methods.includes("onchain_owner") && (
                <button className="btn btn-ghost" onClick={() => void begin("onchain_owner")}>
                  Verify by deployer signature
                </button>
              )}
            </div>
          )}

          {challenge && !status && (
            <div className="mt-12">
              <p>
                {challenge.recordValue.startsWith("Claim ")
                  ? <>Sign this message from <strong>{challenge.recordName}</strong>, then paste the signature:</>
                  : <>Add this TXT record to <strong>{challenge.recordName}</strong>, then check:</>}
              </p>
              <pre className="mono u-d26da97">
                {challenge.recordValue}
              </pre>
              <div className="row gap-8">
                <button className="btn btn-primary" onClick={() => void check()}>Check now</button>
                <button
                  className="btn btn-ghost"
                  onClick={() => void navigator.clipboard?.writeText(challenge.recordValue)}
                >
                  Copy record
                </button>
              </div>
              <p className="muted u-474a305">
                DNS changes can take a few minutes to propagate. The challenge is valid for 72 hours.
              </p>
            </div>
          )}

          {status === "approved" && (
            <p>
              Verified — this listing is yours. <Link to="/dashboard">Manage it →</Link>
            </p>
          )}
          {status === "pending_review" && (
            <p className="muted">
              This listing is already claimed by another organisation, so your claim has gone to a
              moderator rather than transferring automatically.
            </p>
          )}
          {err && <p className="u-497726e">{err}</p>}
        </section>
      )}
    </div>
  );
}
